import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const b64ToBytes = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  return Uint8Array.from(atob((s + pad).replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
};

/** Toggle for Web Push on this browser/device. Renders nothing where push is unsupported. */
export default function PushToggle({ compact = false }) {
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(supported && Notification.permission === "denied");

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then((sub) => setEnabled(!!sub)).catch(() => {});
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setDenied(perm === "denied"); throw new Error("Permission not granted"); }
      const { data: cfg } = await api.get("/push/config");
      if (!cfg.web_push_enabled) throw new Error("Push not configured");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(cfg.vapid_public_key) });
      await api.post("/push/register", { kind: "web", subscription: sub.toJSON(), platform: navigator.userAgent.includes("Mobile") ? "pwa-mobile" : "pwa-desktop" });
      setEnabled(true);
      toast.success("Notifications on — we'll ping you about bookings, reminders and claims");
      api.post("/push/test").catch(() => {});
    } catch (e) {
      toast.error(e.message === "Permission not granted" ? "Allow notifications in your browser to turn this on" : "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.post("/push/unregister", { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      setEnabled(false);
      toast.success("Notifications off on this device");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) return null;

  if (compact) {
    return (
      <button onClick={enabled ? disable : enable} disabled={busy || denied} className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 border ${enabled ? "border-[#1d7ffd]/40 text-[#1d7ffd] bg-[#1d7ffd]/5" : "border-[#E2E8F0] text-[#0a1f4d]"} disabled:opacity-50`} data-testid="push-toggle">
        {enabled ? <BellRing size={13} /> : <Bell size={13} />} {enabled ? "Alerts on" : "Turn on alerts"}
      </button>
    );
  }

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 flex items-start gap-4" data-testid="push-card">
      <span className={`w-11 h-11 rounded-full flex items-center justify-center ${enabled ? "bg-[#1d7ffd]/10 text-[#1d7ffd]" : "bg-[#F0F2F5] text-[#0a1f4d]"}`}>{enabled ? <BellRing size={20} /> : denied ? <BellOff size={20} /> : <Bell size={20} />}</span>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-[#0a1f4d]">Push notifications</p>
        <p className="text-sm text-[#64748B] mt-0.5">
          {denied ? "Notifications are blocked for tadada in your browser settings." : enabled ? "On for this device — booking confirmations, pickup/return reminders and claim updates." : "Get booking confirmations, trip reminders and claim updates on this device — even when the app is closed."}
        </p>
        <div className="mt-3 flex gap-2">
          {!enabled && <button onClick={enable} disabled={busy || denied} className="btn-primary text-xs !py-2 !px-4 disabled:opacity-50" data-testid="push-enable">{busy ? "Enabling…" : "Enable notifications"}</button>}
          {enabled && <button onClick={disable} disabled={busy} className="btn-outline text-xs !py-2 !px-4" data-testid="push-disable">Turn off on this device</button>}
          {enabled && <button onClick={() => api.post("/push/test").then(() => toast.success("Test notification sent"))} className="text-xs font-semibold text-[#64748B] px-3" data-testid="push-test">Send test</button>}
        </div>
      </div>
    </div>
  );
}
